import React, { useState, useRef, useEffect, Fragment } from 'react';
import clsx from 'clsx';

import '@/assets/styles/components/meet-report-form.styles-v2.scss';
import { isValidURL } from '@/utility';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { InteractionExternalMeetingReportRequest } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { toast } from 'react-toastify';
import { PARTICIPANT_TYPE } from '@/constants';
import { useQueryClient } from 'react-query';
import { VideoUploadSection, Button } from '@/components';

interface MeetReportFormProps {
	onSubmit?: (data: MeetReportData) => void;
	defaultOpen?: boolean;
	interactionId: string;
}

export type ParticipantType = (typeof PARTICIPANT_TYPE)[keyof typeof PARTICIPANT_TYPE];

export interface MeetReportData {
	attended: boolean;
	wasSuccessful?: boolean;
	connectionIssue?: boolean;
	otherIssues?: string;
	actualDuration: number;
	isExtendedMeeting: boolean;
	endedBy: ParticipantType;
	recordingFileExternalUrl: string;
	comment: string;
	issueComment: string;
}

const MeetReportFormV2: React.FC<MeetReportFormProps> = ({ onSubmit, interactionId, defaultOpen = false }) => {
	const queryClient = useQueryClient();
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [isStudentJoined, setIsStudentJoined] = useState<boolean>(true);
	const [isEndedSuccessfully, setIsEndedSuccessfully] = useState<boolean>(true);
	const [comment, setComment] = useState<string>('');
	const [otherOptionsComment, setOtherOptionsComment] = useState<string>('');
	const [minutes, setMinutes] = useState<string>('');
	const [isExtendedMeeting, setIsExtendedMeeting] = useState<boolean>(true);
	const [endedBy, setEndedBy] = useState<ParticipantType>(PARTICIPANT_TYPE.EXPERT);
	const [videoDriveLink, setVideoDriveLink] = useState<string>('');
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

	const contentRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState<number>(0);
	const [selectedIssueOptionIdList, setSelectedIssueOptionIdList] = useState([]);

	const { InteractionControllerApi } = useApi();

	const {
		data: interactionData,
		isLoading: interactionDataLoading,
		refetch: interactionDataFeedback
	} = useQueryProvider([QUERY_KEYS.findInteractionById, interactionId], () => InteractionControllerApi.findInteractionById(interactionId), {
		select: ({ data }) => data?.data
	});

	const {
		data: interactionIssueData,
		isLoading: interactionIssueDataLoading,
		refetch: interactionIssueDataFeedback
	} = useQueryProvider([QUERY_KEYS.findExternalReportIssueOptions], () => InteractionControllerApi.findExternalReportIssueOptions(interactionId), {
		select: ({ data }) => data?.data
	});

	const hasExistingReport = interactionData?.externalMeetingReport;

	const saveInteractionForm = useMutationProvider(
		({ interactionId, formData }: { interactionId: string; formData: InteractionExternalMeetingReportRequest }) =>
			InteractionControllerApi.createInteractionExternalReport(interactionId, formData),
		{
			onSuccess: () => {
				toast.success('Geri bildirim başarıyla kaydedildi.');
				queryClient.invalidateQueries([QUERY_KEYS.findInteractionById, interactionId]);
			}
		}
	);

	const updateInteractionForm = useMutationProvider(
		({ interactionId, externalReportId, formData }: { interactionId: string; externalReportId: number; formData: InteractionExternalMeetingReportRequest }) =>
			InteractionControllerApi.updateInteractionExternalReportById(interactionId, externalReportId, formData),
		{
			onSuccess: () => {
				toast.success('Geri bildirim başarıyla güncellendi.');
				queryClient.invalidateQueries([QUERY_KEYS.findInteractionById, interactionId]);
			}
		}
	);

	useEffect(() => {
		if (interactionData?.externalMeetingReport && !isDataLoaded) {
			const report = interactionData.externalMeetingReport;

			setIsStudentJoined(report.isStudentJoined);
			setIsEndedSuccessfully(report.isEndedSuccessfully ?? true);
			setComment(report.comment || '');

			const mins = report.actualDuration || 0;
			setMinutes(mins > 0 ? mins.toString() : '');

			setIsExtendedMeeting(report.isExtendedMeeting ?? true);
			setEndedBy(report.endedBy || PARTICIPANT_TYPE.EXPERT);
			setVideoDriveLink(report.recordingFileExternalUrl || '');
			setOtherOptionsComment(report.issueComment);

			if (report.issueOptionList && report.issueOptionList.length > 0) {
				const issueIds = report.issueOptionList.map(issue => issue.id);
				setSelectedIssueOptionIdList(issueIds);
			}

			setIsDataLoaded(true);
		}
	}, [interactionData, isDataLoaded]);

	useEffect(() => {
		if (contentRef.current) {
			setContentHeight(contentRef.current.scrollHeight);
		}
	}, [isOpen, isStudentJoined, isEndedSuccessfully, comment, minutes, isExtendedMeeting, endedBy, videoDriveLink, errors, selectedIssueOptionIdList]);

	useEffect(() => {
		if (defaultOpen) {
			setIsOpen(true);
		}
	}, [defaultOpen]);

	const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/\D/g, '');
		const numValue = parseInt(value) || 0;

		if (value === '' || (numValue <= 59 && value.length <= 2)) {
			setMinutes(value);
			if (errors.duration) {
				setErrors(prev => {
					const newErrors = { ...prev };
					delete newErrors.duration;
					return newErrors;
				});
			}
		}
	};

	const handleMinutesBlur = () => {
		if (minutes) {
			setMinutes(minutes.padStart(2, '0'));
		}
	};

	const handleMinutesFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		e.target.select();
	};

	const handleVideoDriveLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setVideoDriveLink(e.target.value);
		if (errors.videoDriveLink) {
			setErrors(prev => {
				const newErrors = { ...prev };
				delete newErrors.videoDriveLink;
				return newErrors;
			});
		}
	};

	const handleOtherIssuesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setComment(e.target.value);
	};

	const handleOtherOptionsComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setOtherOptionsComment(e.target.value);
	};

	const handleIssueOptionChange = value => {
		const hasIssue = selectedIssueOptionIdList?.includes(value);

		if (hasIssue) {
			const filteredIssues = selectedIssueOptionIdList?.filter(issue => issue !== value);
			setSelectedIssueOptionIdList(filteredIssues);
		} else {
			setSelectedIssueOptionIdList([...selectedIssueOptionIdList, value]);
		}
	};

	const validateForm = (): boolean => {
		const newErrors: { [key: string]: string } = {};

		if (isStudentJoined) {
			const durationMinutes = parseInt(minutes) || 0;
			if (durationMinutes === 0) {
				newErrors.duration = 'Lütfen görüşme süresini giriniz';
			}

			if (videoDriveLink?.length > 0 && !isValidURL(videoDriveLink.trim())) {
				newErrors.videoDriveLink = 'Lütfen geçerli bir URL giriniz (örn: https://drive.google.com/...)';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		let data = {};
		if (!isStudentJoined) {
			data = {
				isStudentJoined,
				comment
			};
		} else {
			data = {
				isStudentJoined,
				isEndedSuccessfully,
				...(!isEndedSuccessfully && {
					issueOptionIdList: [...selectedIssueOptionIdList]
				}),
				actualDuration: parseInt(minutes) || 0,
				isExtendedMeeting,
				endedBy,
				comment,
				issueComment: otherOptionsComment,
				recordingFileExternalUrl: videoDriveLink
			};
		}

		try {
			if (hasExistingReport) {
				await updateInteractionForm.mutateAsync({
					interactionId: interactionId,
					externalReportId: hasExistingReport.id,
					formData: data
				});
			} else {
				await saveInteractionForm.mutateAsync({
					interactionId: interactionId,
					formData: data
				});
			}
			onSubmit?.(data);
		} catch (error) {
			console.error('Form submission error:', error);
		}
	};

	const isSubmitting = saveInteractionForm.isLoading || updateInteractionForm.isLoading;

	return (
		<Fragment>
			<div className="meet-report-form">
				<div
					ref={contentRef}
					className={clsx('meet-report-form__content is-open')}>
					<h6 className="meet-report-form-header">Meet Görüşmesi Değerlendirme</h6>
					<p className="meet-report-form-header-description">Öğrenci ile yaptığınız meet görüşmesine dair formu doldurabilirsiniz.</p>
					<div className="meet-report-form__content-inner">
						{hasExistingReport && (
							<div className="meet-report-form__field">
								<div
									className="meet-report-form__info-box"
									style={{
										padding: '12px 16px',
										backgroundColor: 'var(--surface-f0f9ff)',
										borderRadius: '8px',
										marginBottom: '16px',
										fontSize: '14px',
										color: 'var(--text-0369a1)'
									}}>
									Bu rapor daha önce kaydedilmiştir. Değişiklik yaparak güncelleyebilirsiniz.
								</div>
							</div>
						)}

						<div className="meet-report-form__field">
							<div className="meet-report-form__field-label">Meet üzerinden yaptığınız görüşmeye öğrenci katıldı mı?</div>
							<div className="meet-report-form__radio-group">
								<label className="meet-report-form__radio">
									<input
										type="radio"
										name="isStudentJoined"
										checked={isStudentJoined}
										onChange={() => setIsStudentJoined(true)}
									/>
									<span className="meet-report-form__radio-custom"></span>
									<span className="meet-report-form__radio-label">Katıldı</span>
								</label>
								<label className="meet-report-form__radio">
									<input
										type="radio"
										name="isStudentJoined"
										checked={!isStudentJoined}
										onChange={() => setIsStudentJoined(false)}
									/>
									<span className="meet-report-form__radio-custom"></span>
									<span className="meet-report-form__radio-label">Katılmadı</span>
								</label>
							</div>
						</div>

						{isStudentJoined && (
							<>
								<div className="meet-report-form__field">
									<div className="meet-report-form__field-label">Meet üzerinden yaptığınız görüşme başarıyla gerçekleşti mi?</div>
									<div className="meet-report-form__radio-group">
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="isEndedSuccessfully"
												checked={isEndedSuccessfully}
												onChange={() => setIsEndedSuccessfully(true)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Evet</span>
										</label>
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="successful"
												checked={!isEndedSuccessfully}
												onChange={() => setIsEndedSuccessfully(false)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Hayır</span>
										</label>
									</div>
								</div>

								{!isEndedSuccessfully && (
									<div className="meet-report-form__field">
										<div className="meet-report-form__field-label">Yaşanan sorunlar (birden fazla seçilebilir)</div>
										<div className="meet-report-form__checkbox-group">
											{interactionIssueData?.map(issue => (
												<label
													key={issue?.id}
													className="meet-report-form__checkbox">
													<input
														type="checkbox"
														checked={selectedIssueOptionIdList?.includes(issue?.id)}
														onChange={() => handleIssueOptionChange(issue?.id)}
													/>
													<span className="meet-report-form__checkbox-custom"></span>
													<span className="meet-report-form__checkbox-label">{issue?.description}</span>
												</label>
											))}
										</div>
										<div
											className="meet-report-form__field-label"
											style={{ marginTop: '16px' }}>
											Diğer sorunlar
										</div>
										<textarea
											value={otherOptionsComment}
											onChange={handleOtherOptionsComment}
											placeholder="Varsa diğer sorunları buraya yazın..."
											className="meet-report-form__textarea"
											rows={4}
										/>
									</div>
								)}

								<div className="meet-report-form__field">
									<div className="meet-report-form__field-label">
										Görüşmeniz toplam kaç dakika sürdü?
										<br />
										<span>
											(Birebir Uzman Platformu’ndan başlatıp Meet üzerinden devam ettiyseniz, lütfen tüm sürenin toplamını yazın.{' '}
											<u>Ödemeniz, burada belirttiğiniz toplam süre üzerinden yapılacaktır.</u>)
										</span>
									</div>
									<div className="meet-report-form__duration">
										<div className="meet-report-form__duration-labels">
											<span>Dakika</span>
										</div>
										<div className="meet-report-form__duration-inputs">
											<input
												type="text"
												value={minutes}
												onChange={handleMinutesChange}
												onBlur={handleMinutesBlur}
												onFocus={handleMinutesFocus}
												maxLength={2}
												placeholder="00"
												className={clsx('meet-report-form__duration-input', {
													error: errors.duration
												})}
											/>
										</div>
										{errors.duration && <div className="meet-report-form__error">{errors.duration}</div>}
									</div>
								</div>

								<div className="meet-report-form__field">
									<div className="meet-report-form__field-label">Meet üzerinden yapmış olduğunuz görüşme uzatıldı mı?</div>
									<div className="meet-report-form__radio-group">
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="extended"
												checked={isExtendedMeeting}
												onChange={() => setIsExtendedMeeting(true)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Evet, uzatıldı</span>
										</label>
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="extended"
												checked={!isExtendedMeeting}
												onChange={() => setIsExtendedMeeting(false)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Hayır, uzatılmadı</span>
										</label>
									</div>
								</div>

								<div className="meet-report-form__field">
									<div className="meet-report-form__field-label">Meet üzerinden yapmış olduğunuz görüşmeyi kim sonlandırdı?</div>
									<div className="meet-report-form__radio-group">
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="terminated"
												checked={endedBy === PARTICIPANT_TYPE.EXPERT}
												onChange={() => setEndedBy(PARTICIPANT_TYPE.EXPERT)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Uzman</span>
										</label>
										<label className="meet-report-form__radio">
											<input
												type="radio"
												name="terminated"
												checked={endedBy === PARTICIPANT_TYPE.STUDENT}
												onChange={() => setEndedBy(PARTICIPANT_TYPE.STUDENT)}
											/>
											<span className="meet-report-form__radio-custom"></span>
											<span className="meet-report-form__radio-label">Öğrenci</span>
										</label>
									</div>
								</div>

								<div className="meet-report-form__field">
									<div className="meet-report-form__field-label">Meet üzerinden yaptığınız görüşmenizin video drive linkini aşağıdaki alana yapıştırın</div>
									<input
										type="text"
										value={videoDriveLink}
										onChange={handleVideoDriveLinkChange}
										placeholder="Linki bu alana yapıştırın"
										className={clsx('meet-report-form__input', {
											error: errors.videoDriveLink
										})}
									/>
									{errors.videoDriveLink && <div className="meet-report-form__error">{errors.videoDriveLink}</div>}

									<div className="meet-report-form__info">
										<i className="icons-info-circle" />
										<span>
											Görüşme kaydının Drive linkini bu alana ekleyin. Drive’a yükleyemediyseniz, formu kaydettikten sonra açılan yükleme alanından videoyu iletmeniz gerekir.{' '}
											<b>Aksi halde ücret ödemesi yapılmaz.</b>
										</span>
									</div>
								</div>
							</>
						)}

						<div className="meet-report-form__field-label">Meet görüşmesi deneyiminizle ilgili bizimle paylaşmak istediğiniz bir yorum ya da geri bildiriminiz var mı?</div>
						<textarea
							value={comment}
							onChange={handleOtherIssuesChange}
							placeholder="Varsa diğer notlarınızı buraya yazın..."
							className="meet-report-form__textarea"
							rows={4}
						/>

						<Button
							variant="primary"
							size="small"
							loading={isSubmitting}
							disabled={isSubmitting}
							onClick={handleSubmit}
							className="meet-report-form__submit">
							<span className="meet-report-form__submit__content">
								<i className="icons-diskette" />

								<span>{hasExistingReport ? 'Raporu Güncelle' : 'Raporu Kaydet'}</span>
							</span>
						</Button>
					</div>
				</div>
			</div>
			{interactionData?.externalMeetingReport?.id && !interactionData?.externalMeetingReport?.recordingFileExternalUrl && (
				<VideoUploadSection
					interactionId={interactionId}
					externalReportId={interactionData.externalMeetingReport.id}
					externalMeetingReport={interactionData?.externalMeetingReport}
					onUploadSuccess={() => {
						queryClient.invalidateQueries([QUERY_KEYS.findInteractionById, interactionId]);
						toast.success('Video başarıyla yüklendi!');
					}}
				/>
			)}
		</Fragment>
	);
};

export default MeetReportFormV2;
